<template>
  <div class="w-full flex flex-col items-center">
    <div class="w-full">
      <StripeExpressCheckout
        v-if="stripe && elements && showPaymentForm"
        :tos="quickCheckoutTos"
        :show="!hideExpressCheckout"
        :stripe="stripe"
        :elements="elements"
        :disabled="shoppingCartLoading"
        @quick-checkout-load="onQuickCheckoutLoad"
        @confirm="onQuickCheckoutConfirm"
      />

      <template v-if="!hideForm">
        <VPLoadingSpinner
          :loading="!addressElementReady || (!paymentElementReady && showPaymentForm)"
          use-show
        >
          <Transition>
            <div
              v-if="showOrSeparator"
              class="flex justify-center items-center gap-4 text-vp-base-400"
            >
              <hr class="grow border">
              <span class="uppercase">{{ $gettext('or') }}</span>
              <hr class="grow border">
            </div>
          </Transition>

          <main
            v-if="!hideForm"
            class="p-2 sm:p-4 rounded-md overflow-hidden w-full h-fit bg-vp-base-150"
          >
            <form
              id="payment-form"
              @submit.prevent="handlePayment"
            >
              <h3 class="font-bold my-2 sm:my-4">
                {{ $gettext('Contact info') }}
              </h3>
              <div class="relative mb-4">
                <input
                  id="email"
                  v-model="emailRef"
                  type="email"
                  :placeholder="$gettext('Email')"
                  autocomplete="email"
                  class="input-floating-label peer"
                  @blur="onEmailUpdate"
                >
                <label
                  for="email"
                  class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                >
                  {{ $gettext('Email') }}
                </label>
              </div>
              <template v-if="event?.config?.features?.order_company_info">
                <div class="relative mb-4">
                  <input
                    id="company-name-input"
                    v-model="orderDetails.customerInformation.companyName"
                    type="text"
                    :placeholder="$gettext('Company name (optional)')"
                    class="input-floating-label peer"
                  >
                  <label
                    for="company-name-input"
                    class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                  >
                    {{ $gettext('Company name (optional)') }}
                  </label>
                </div>

                <div class="relative mb-4">
                  <input
                    id="vat-id-input"
                    v-model="orderDetails.customerInformation.vatId"
                    type="text"
                    :placeholder="$gettext('VAT ID (optional)')"
                    class="input-floating-label peer"
                  >
                  <label
                    for="vat-id-input"
                    class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                  >
                    {{ $gettext('VAT ID (optional)') }}
                  </label>
                </div>
              </template>
              <div ref="addressElementRef" />
              <template v-if="showPaymentForm">
                <h3 class="font-bold my-2 sm:my-4">
                  {{ $gettext('Payment') }}
                </h3>
                <div ref="paymentElementRef" />
              </template>
            </form>
          </main>

          <CheckoutCheckboxes
            v-if="showAgreementCheckboxes"
            :checkboxes="visibleCheckboxes"
            @update="v => (checkedCheckboxes = v)"
            @required-satisfied="v => (requiredSatisfied = v)"
          />

          <div
            v-if="!hideSubmit"
            class="w-full py-4"
          >
            <VPButton
              type="submit"
              class="block w-full uppercase disabled:cursor-not-allowed"
              :disabled="isLoading || !isFormComplete"
              :label="$gettext('Confirm purchase')"
              variant="solid"
              size="xl"
              min-width="100%"
              @click="handlePayment"
            />
          </div>

          <Transition>
            <slot
              v-if="showExpressCheckout"
              name="footer"
            />
          </Transition>
        </VPLoadingSpinner>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import {
  Appearance,
  loadStripe,
  Stripe,
  StripeElements, StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { useCheckoutStore } from '@/stores/checkout';
import { useGettext } from 'vue3-gettext';
import VPButton from '@/components/form/VPButton.vue';
import { useRouter } from 'vue-router';
import CheckoutCheckboxes from '@/checkout/CheckoutCheckboxes.vue';
import { useOrderDetails } from '@/checkout/useOrderDetails';
import { Checkout } from '@vp/common';
import * as VPError from '@/types/VPError';
import { useToast } from 'vue-toastification';
import StripeExpressCheckout from '@/checkout/StripeExpressCheckout.vue';
import * as Sentry from '@sentry/vue';
import { useHive } from '@/checkout/integrations/useHive';
import { VPLoadingSpinner } from '@vp/vue-components';
import { LoadingState } from '@/util/LoadingState';

const { ShoppingCartItemInput, Utils } = Checkout;

type Props = {
  hideExpressCheckout?: boolean;
  hideForm?: boolean;
  hideSubmit?: boolean;
  hideCheckboxes?: string[];
  amount?: number;
  showAgreementCheckboxes?: boolean;
  quickCheckoutTos?: Checkout.CheckoutCheckboxConfig.CheckoutCheckboxConfig;
  paymentIntent?: string;
  paymentIntentSecret?: string;
  orderFingerprint?: string;
};

const props = withDefaults(defineProps<Props>(), {
  hideCheckboxes: () => [],
  amount: 0,
  quickCheckoutTos: undefined,
});

const showOrSeparator = computed(() => showExpressCheckout.value && !props.hideForm && showPaymentForm.value);

const addressElementRef = ref<HTMLElement | undefined>();
const paymentElementRef = ref<HTMLElement | undefined>();

const { $gettext } = useGettext();
const router = useRouter();

const isLoading = ref(false);
const messages = ref([]);
const stripe = ref<Stripe | null>(null);
const elements = ref<StripeElements>();
const emailRef = ref<string>('');
const isAddressComplete = ref(false);
const isPaymentReady = ref(false);
const isPhoneEntered = ref(false);
const addressElementReady = ref(false);
const paymentElementReady = ref(false);

const emit = defineEmits<{
  (e: 'quick-checkout-load'): void;
}>();

const showExpressCheckout = ref(false);

const checkoutStore = useCheckoutStore();
const processorConfig = computed(() => checkoutStore.getEventCheckoutConfig);
const event = computed<Checkout.CheckoutEvent.CheckoutEvent | undefined>(() => checkoutStore.getSelectedEvent);
const eventId = computed(() => checkoutStore.getSelectedEvent.id);
const checkboxes = computed<Checkout.CheckoutCheckboxConfig.CheckoutCheckboxConfig[]>(
  () => checkoutStore.getEventCheckoutConfig?.checkboxes || [],
);
const checkedCheckboxes = ref<Record<string, boolean>>({});
const requirePhone = computed(() => checkoutStore.getEventCheckoutConfig.requirePhoneNumber);
const requiredSatisfied = ref<boolean>(false);
const hive = ref<ReturnType<typeof useHive> | null>(null);
const shoppingCartLoading = computed(() => checkoutStore.shoppingCartLoadingState === LoadingState.LOADING);

const visibleCheckboxes = computed(() => checkboxes.value.filter(({ key }) => !props.hideCheckboxes.includes(key)));

const { orderDetails, setAddress, setCustomer, setAgreements } = useOrderDetails();
const toast = useToast();

const clientSecret = ref<string | null>(props.paymentIntentSecret || null);
const paymentIntentId = ref<string | null>(props.paymentIntent || null);
const paymentIntentDetails = ref<unknown | undefined>();

const fetchPaymentIntentDetails = async (id: string) => {
  const pi = await stripe.value?.retrievePaymentIntent(id);
  if (pi?.paymentIntent) {
    paymentIntentDetails.value = pi.paymentIntent;
  }
};

const updatePaymentInformation = async () => {
  let paymentInformations, checkout, errors;
  if (paymentIntentId.value) {
    ({ checkout, errors } = await checkoutStore.updatePaymentInformation({ paymentIntentId: paymentIntentId.value }));
    paymentInformations = checkout?.updatePaymentInformation.paymentInformation || [];
    elements.value?.fetchUpdates();
  } else {
    ({ checkout, errors } = await checkoutStore.createPaymentInformation());
    paymentInformations = checkout?.createPaymentInformation.paymentInformation || [];
  }

  if (errors) {
    throw VPError.getNew(errors.map(({ message }) => message));
  }

  const paymentInformation = paymentInformations.find(
    ({ eventId: id }) => id === eventId.value,
  );

  if (!paymentInformation) {
    throw VPError.getNew([$gettext('Error retrieving payment information.')]);
  }

  clientSecret.value = paymentInformation.paymentIntent.clientSecret;
  paymentIntentId.value = paymentInformation.paymentIntent.id;
};

const finalizeUrl = (orderId: string): string => {
  const finalize = router.resolve({ name: 'OrderFinalize', params: { eventId: eventId.value }, query: { shoppingCartToken: checkoutStore.shoppingCartToken, orderId } });
  return new URL(finalize.fullPath, window.location.origin).href;
};

const checkoutUrl = (orderId: string): string => {
  const finalize = router.resolve({ name: 'OrderPayment', params: { eventId: eventId.value }, query: { shoppingCartToken: checkoutStore.shoppingCartToken, orderId } });
  return new URL(finalize.fullPath, window.location.origin).href;
};

const showPaymentForm = computed(() => !paymentIntentDetails.value || paymentIntentDetails.value?.status !== 'requires_capture');

onMounted(async () => {
  hive.value = useHive(checkoutStore.getSelectedEvent.config.hiveBrandId);
  stripe.value = await loadStripe(processorConfig.value?.processorPublicKey, {
    stripeAccount: processorConfig.value?.processorAccountId,
  });

  // TODO: This is a quick fix to allow handling the flow when some data (phone no) is missing from quick checkout.
  //       In that case, we present the customer details form, but hide the payment section.
  if (props.paymentIntentSecret) {
    await fetchPaymentIntentDetails(props.paymentIntentSecret);
  }
  if (props.orderFingerprint) {
    await checkoutStore.loadOrderSummary(props.orderFingerprint);
    if (checkoutStore.orderSummary) {
      emailRef.value = checkoutStore.orderSummary?.customer?.email;
    }
  }

  watch(() => clientSecret.value, () => {
    if (clientSecret.value) {
      initElements(clientSecret.value);
    }
  }, { immediate: true });

  if (paymentIntentDetails.value?.status === 'requires_capture') {
    isPaymentReady.value = true;
  } else {
    watch(
      () => props.amount,
      () => updatePaymentInformation(),
      { immediate: true },
    );
  }
});

const initElements = async (clientSecret: string) => {
  if (!eventId.value) {
    return;
  }

  try {
    const appearance: Appearance = {
      theme: 'stripe',
      labels: 'floating',
    };

    elements.value = stripe.value?.elements({
      clientSecret,
      appearance,
      loader: 'always',
    });

    if (addressElementRef.value) {
      initAddressElement(addressElementRef.value);
    }

    if (paymentElementRef.value) {
      initPaymentElement(paymentElementRef.value);
    }
  } catch (e: VPError | unknown) {
    if (e.type == 'VPError') {
      const vpError = e as VPError;
      for (const msg of vpError.errors) {
        toast.error(msg);
      }

      return;
    }

    throw e;
  } finally {
    isLoading.value = false;
  }
};

const initAddressElement = (element: HTMLElement): void => {
  if (!elements.value) {
    throw new Error('elements is not initialized');
  }

  let defaultValues = {};
  if (checkoutStore.orderSummary) {
    toast.info($gettext('Please, fill in the missing fields before proceeding to payment.'));
    const os = checkoutStore.orderSummary as Checkout.OrderSummary.OrderSummary;
    let address = {};
    if (os.address) {
      address = {
        line1: os.address.street1,
        line2: os.address.street2,
        city: os.address.city,
        state: os.address.state,
        postal_code: os.address.postalCode,
        country: os.address.country,
      };
    }

    defaultValues = {
      firstName: os.customer?.firstName,
      lastName: os.customer?.lastName,
      phone: os.customer?.phoneNumber,
      address,
    };
  }

  const addressElement = elements.value.create('address', {
    mode: 'billing',
    display: { name: 'split' },
    fields: { phone: 'always' },
    validation: {
      phone: {
        required: requirePhone.value ? 'always' : 'auto',
      },
    },
    defaultValues,
  });

  addressElement.mount(element);

  addressElement.on('ready', () => {
    addressElementReady.value = true;
  });

  addressElement.on('change', event => {
    isAddressComplete.value = event.complete;
    isPhoneEntered.value = !!event.value.phone;
  });
};

const initPaymentElement = (element: HTMLElement): void => {
  if (!elements.value) {
    throw new Error('elements is not initialized');
  }

  const paymentElement = elements.value!.create('payment');
  paymentElement.mount(element);

  paymentElement.on('ready', () => {
    paymentElementReady.value = true;
  });

  paymentElement.on('change', event => {
    isPaymentReady.value = event.complete;
  });
};

const handlePayment = async () => {
  if (!stripe.value || !elements.value) {
    messages.value.push($gettext('Stripe elements are not initialized.'));
    return;
  }

  if (!isAddressComplete.value || !isPaymentReady.value) {
    messages.value.push($gettext('Please complete all required fields.'));
    return;
  }

  if (isLoading.value) {
    return;
  }

  const addressElement = elements.value.getElement('address');
  const { complete, value } = await addressElement.getValue();

  if (!complete) {
    return;
  }

  try {
    isLoading.value = true;

    orderDetails.value.eventId = eventId.value;
    setAddress(value.address);
    setCustomer({
      firstName: value.firstName,
      lastName: value.lastName,
      phoneNumber: value.phone,
      email: emailRef.value,
    });
    setAgreements(Object.keys(checkedCheckboxes.value).map(key => ({ key, value: checkedCheckboxes.value[key] })));

    await confirmPayment();
  } finally {
    isLoading.value = false;
  }
};

const onQuickCheckoutLoad = () => {
  emit('quick-checkout-load');
  showExpressCheckout.value = true;
};

const onQuickCheckoutConfirm = async (confirmEvent: StripeExpressCheckoutElementConfirmEvent) => {
  orderDetails.value.eventId = eventId.value;
  if (confirmEvent.billingDetails) {
    setAddress(confirmEvent.billingDetails.address);
    setCustomer({
      name: confirmEvent.billingDetails.name,
      email: confirmEvent.billingDetails.email,
      phoneNumber: confirmEvent.billingDetails.phone,
    });
  }
  setAgreements([
    ...Object.keys(checkedCheckboxes.value).map(key => ({ key, value: checkedCheckboxes.value[key] })),
    { key: 'terms_of_service', value: true },
  ]);

  confirmPayment();
};

const confirmPayment = async () => {
  const { orderSummary } = await checkoutStore.saveOrder(orderDetails.value);
  const orderId = orderSummary.fingerprint;

  try {
    hive.value?.subscribeCustomerToHive({
      ...orderDetails.value.customerInformation.customer,
      zipCode: orderDetails.value.customerInformation.address.postalCode,
      country: orderDetails.value.customerInformation.address.country,
      city: orderDetails.value.customerInformation.address.city,
      state: orderDetails.value.customerInformation.address.state,
      mailAddress: orderDetails.value.customerInformation.address.street1,
      didOptIn: (orderDetails.value.customerInformation.agreements.find(({ key }: { key: string }) => key == 'newsletter') || {})['value'],
      didSmsOptIn: (orderDetails.value.customerInformation.agreements.find(({ key }: { key: string }) => key == 'sms_newsletter') || {})['value'],
    });
  } catch (e) {
    Sentry.captureException(e);
  }

  if (!stripe.value || !elements.value) {
    toast.error($gettext('An unexpected error has occurred.'));
    throw new Error('Stripe/elements are not initialized.');
  }

  let return_url = finalizeUrl(orderId);
  if (orderSummary.state === 'draft') {
    toast.warning($gettext('Please, fill in the missing information in the form before proceeding to payment.'));
    return_url = checkoutUrl(orderId);
  }

  if (paymentIntentDetails.value?.status === 'requires_capture') {
    const finalize = router.resolve({ name: 'OrderFinalize', params: { eventId: eventId.value }, query: { shoppingCartToken: checkoutStore.shoppingCartToken, orderId: orderSummary.fingerprint, payment_intent: paymentIntentDetails.value.id } });
    window.location.href = finalize.href;
    return;
  }

  const { error } = await stripe.value.confirmPayment({
    elements: elements.value,
    confirmParams: {
      return_url,
    },
    redirect: 'always',
  });

  if (error) {
    if (error.type === 'card_error' || error.type === 'validation_error') {
      toast.error(error.message);
    } else {
      toast.error($gettext('An unexpected error has occurred.'));
      Sentry.captureMessage('Quick checkout unexpected error', {
        extra: {
          error,
        },
      });
    }
  }
};

const isFormComplete = computed(() => {
  return emailRef.value?.trim() !== '' && isAddressComplete.value && isPaymentReady.value && requiredSatisfied.value;
});

const onEmailUpdate = () => {
  if (!emailRef.value || !event.value?.config) {
    return;
  }

  if (Utils.featureEnabled(event.value.config, 'joinit')) {
    applyDiscount(emailRef.value);
  }
};

const applyDiscount = async (code: string) => {
  if (!code || code == '') {
    return;
  }

  const discount = ShoppingCartItemInput.newDiscount(code);
  try {
    await checkoutStore.storeShoppingCartItem(discount);
    toast.success($gettext('Discount applied'));
  } catch (e: VPError | unknown) {
    if (e.type == 'VPError') {
      //
    } else {
      toast.error($gettext('Error applying discount'));
      throw e;
    }
  }
};
</script>
